import { Box, IconButton, Paper, Stack, Toolbar, Tooltip } from '@mui/material';
import { EditCommentIcon, MessageIcon, ThemeExtension } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { LockIcon } from '@synergy/theme-next';
import { PoolBanner } from '@synergy/components-next';
import { PropsWithChildren, ReactNode, useState } from 'react';
import { ReceiptAlert, ReceiptAlertType } from '@synergy/models';
import { makeStyles } from '@mui/styles';

import { PoolSubToolbarAlertModal } from './pool-sub-toolbar-alert-modal';
import { PoolTypography } from '../typography/pool-typography';

const useStyles = makeStyles(() => ({
    container: {
        borderRadius: 0,
        marginBottom: 0,
        boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.12)', // magic
    },
    subToolbar: {
        padding: '0 ' + ThemeExtension.general.pageContentSpacing,
        minHeight: 72,
    },
    subToolbarChildrenWrapper: {
        marginLeft: 'auto',
        gap: '24px',
        display: 'inline-flex',
        alignItems: 'center',
    },
}));

export enum AlertSeverity {
    Error = 'error',
    Warning = 'warning',
    Info = 'info',
}

interface PoolSubToolbarProps {
    titleMessage?: ReactNode;
    className?: string;
    isChildToolbar?: boolean;
    alert?: ReceiptAlert;
    isLocked?: boolean;
    onAlertChange?: (alert: ReceiptAlert | null) => void;
}

export const PoolSubToolbar = (props: PropsWithChildren<PoolSubToolbarProps>) => {
    const classes = useStyles();
    const [showAlertModal, setShowAlertModal] = useState(false);

    const getToolbarClasses = () => {
        if (props.className) {
            return `${classes.container} ${props.className}`;
        } else {
            return classes.container;
        }
    };

    const handleAlertChange = (alert: ReceiptAlert) => {
        setShowAlertModal(false);
        props.onAlertChange?.(alert);
    };

    const handleAlertDelete = () => {
        setShowAlertModal(false);
        props.onAlertChange?.(null);
    };

    const getAlertSeverity = () => {
        switch (props.alert?.type) {
            case ReceiptAlertType.Danger:
                return AlertSeverity.Error;
            case ReceiptAlertType.Warning:
                return AlertSeverity.Warning;
            default:
                return AlertSeverity.Info;
        }
    };

    return (
        <Paper className={getToolbarClasses()} elevation={2}>
            <Toolbar className={classes.subToolbar}>
                <Stack sx={{ width: '100%' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={1} flex="1" sx={{ width: '100%' }}>
                            {props.titleMessage && (
                                <>
                                    <PoolTypography
                                        component="span"
                                        typography={props.isChildToolbar ? 'h5' : 'h4'}
                                        color={props.isLocked ? 'error' : 'inherit'}
                                    >
                                        <Stack gap={1} flexDirection={'row'} alignItems={'center'}>
                                            {props.isLocked && <LockIcon />}
                                            {props.titleMessage}
                                        </Stack>
                                    </PoolTypography>
                                    {props.onAlertChange && (
                                        <Tooltip
                                            title={
                                                props.alert ? (
                                                    <FormattedMessage id="components.receiptAlert.title.edit" />
                                                ) : (
                                                    <FormattedMessage id="components.receiptAlert.title.add" />
                                                )
                                            }
                                        >
                                            <IconButton onClick={() => setShowAlertModal(true)}>
                                                {props.alert ? <EditCommentIcon /> : <MessageIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </Stack>
                        <Box color="grey.800" className={classes.subToolbarChildrenWrapper}>
                            {props.children}
                        </Box>
                    </Stack>
                    {props.alert && (
                        <PoolBanner variant="outlined" sx={{ mb: 2 }} severity={getAlertSeverity()}>
                            {props.alert.text}
                        </PoolBanner>
                    )}
                </Stack>
            </Toolbar>

            <PoolSubToolbarAlertModal
                isVisible={showAlertModal}
                onClose={() => setShowAlertModal(false)}
                alert={props.alert}
                onAlertChange={handleAlertChange}
                onAlertDelete={handleAlertDelete}
            />
        </Paper>
    );
};
